import {
    IPsoAgreementCommonFormFields,
    IPsoAgreementViewModel
} from "../../../../services/booking/models/pso/agreements/base/pso-agreement.view-mode.interface";
import {
    IPsoAgreementsViewModel
} from "../../../../services/booking/models/pso/agreements/base/pso-agreements.view-model.interface";
import {observer} from "mobx-react";
import styled from "styled-components";
import {useServices} from "../../../../hooks/use-services.hook";
import React, {PropsWithChildren, useState} from "react";
import {DotsAnimationComponent} from "../../../dots-animation/dots-animation.component";
import {IonIcon} from "@ionic/react";
import {downloadOutline, sendOutline} from "ionicons/icons";
import {PsoAgreementComponent} from "./pso-agreement.component";
import {NeutralButton} from "../../../../basic-components/buttons/neutral-button";
import {PrimaryButton} from "../../../../basic-components/buttons/primary-button";
import {InfoLineBox} from "./boxes/pso-agreement.boxes";
import {ReadonlyCheckboxComponent} from "../../../../basic-components/checkbox/readonly-checkbox.component";

const ContainerBox = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: ${props => props.theme.spacing.flexGap24};
    width: 100%;
`

const ButtonContentBox = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: ${props => props.theme.spacing.flexGap8};
    width: 100%;
    
`



const ConfirmationCheckBoxLabel = styled.div`
    text-wrap: wrap;
`

const ConfirmationCheckBox = styled(ReadonlyCheckboxComponent)<{$isDownloaded: boolean}>`
    color: ${props => props.checked ? props.theme.colors.successShade : props.theme.colors.danger};
    cursor: ${props => props.$isDownloaded ? "not-allowed" : "pointer"};
`

interface PsoAgreementsComponentProps<TFields extends IPsoAgreementCommonFormFields,
                                      TAgreement extends IPsoAgreementViewModel<TFields>,
                                      TPsoAgreements extends IPsoAgreementsViewModel<TFields, TAgreement>> extends PropsWithChildren {

    psoAgreements: TPsoAgreements;
    renderHeader: () => React.ReactElement;
}

export const PsoAgreementsComponent =  observer(<TFields extends IPsoAgreementCommonFormFields,
                                                                        TAgreement extends IPsoAgreementViewModel<TFields>,
                                                                        TPsoAgreements extends IPsoAgreementsViewModel<TFields, TAgreement>>(props: PsoAgreementsComponentProps<TFields, TAgreement, TPsoAgreements>) => {

    const services = useServices();
    const [isDownloadInProgress, setIsDownloadInProgress] = useState<boolean>(false);


    const psoAgreements = props.psoAgreements;



    const onConfirmation = () => {

        if(psoAgreements.confirmed) {
            psoAgreements.confirmed = false;
        } else if(psoAgreements.activateValidationErrors().length === 0) {
            psoAgreements.confirmed = true;
        }
    }


    const onDownload = async () => {
        if(isDownloadInProgress) {
            return;
        }

        setIsDownloadInProgress(true);
        try {
            await psoAgreements.downloadDeclaration();
        } finally {
            setIsDownloadInProgress(false);
        }

        psoAgreements.downloaded = true;
    }



    const onSubmit = () => {
        psoAgreements.submitted = true;
    }

    const renderDownloadButtonContent = () => {
        if(isDownloadInProgress) {
            return (<DotsAnimationComponent/>);
        }

        return (
            <ButtonContentBox>
                <span>
                    {services.language.translate('Downloading declaration')}
                </span>
                <IonIcon icon={downloadOutline}/>
            </ButtonContentBox>

        )
    }

    const renderIndividualAgreements = () => {
        return psoAgreements.agreements.map(agr => <PsoAgreementComponent key={agr.passengerKey} agreement={agr}/>)
    }

    return (
        <ContainerBox>
            {props.renderHeader()}
            {renderIndividualAgreements()}

            {props.children}

            <InfoLineBox>
                <ConfirmationCheckBox checked={psoAgreements.confirmed} $isDownloaded={psoAgreements.downloaded}
                                      label={<ConfirmationCheckBoxLabel>{services.language.translate('I confirm that the information provided is true and correct')}</ConfirmationCheckBoxLabel>}
                                      labelPlacement={"end"}
                                      onClick={onConfirmation}/>
            </InfoLineBox>

            <InfoLineBox>
                <NeutralButton fullWidth={true} onClick={onDownload} disabled={!psoAgreements.confirmed && services.configuration.isProduction}>
                    {renderDownloadButtonContent()}
                </NeutralButton>
            </InfoLineBox>

            <InfoLineBox>
                <PrimaryButton fullWidth={true} onClick={onSubmit} disabled={!psoAgreements.confirmed || !psoAgreements.downloaded}>
                    <ButtonContentBox>
                        <span>
                            {services.language.translate('Submit declaration')}
                        </span>
                        <IonIcon icon={sendOutline}/>
                    </ButtonContentBox>

                </PrimaryButton>
            </InfoLineBox>
        </ContainerBox>
    )
})